/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import { getAccountMetaFactory, type ResolvedAccount } from "../shared";

export const INITIALIZE_STAKING_CONFIG_DISCRIMINATOR = new Uint8Array([
  78, 164, 6, 115, 206, 48, 168, 105,
]);

export function getInitializeStakingConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    INITIALIZE_STAKING_CONFIG_DISCRIMINATOR,
  );
}

export type InitializeStakingConfigInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountStakingConfig extends string | AccountMeta<string> = string,
  TAccountAuthority extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountStakingConfig extends string
        ? WritableAccount<TAccountStakingConfig>
        : TAccountStakingConfig,
      TAccountAuthority extends string
        ? WritableSignerAccount<TAccountAuthority> &
            AccountSignerMeta<TAccountAuthority>
        : TAccountAuthority,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      ...TRemainingAccounts,
    ]
  >;

export type InitializeStakingConfigInstructionData = {
  discriminator: ReadonlyUint8Array;
  minStake: bigint;
  treasury: Address;
};

export type InitializeStakingConfigInstructionDataArgs = {
  minStake: number | bigint;
  treasury: Address;
};

export function getInitializeStakingConfigInstructionDataEncoder(): FixedSizeEncoder<InitializeStakingConfigInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["minStake", getU64Encoder()],
      ["treasury", getAddressEncoder()],
    ]),
    (value) => ({
      ...value,
      discriminator: INITIALIZE_STAKING_CONFIG_DISCRIMINATOR,
    }),
  );
}

export function getInitializeStakingConfigInstructionDataDecoder(): FixedSizeDecoder<InitializeStakingConfigInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["minStake", getU64Decoder()],
    ["treasury", getAddressDecoder()],
  ]);
}

export function getInitializeStakingConfigInstructionDataCodec(): FixedSizeCodec<
  InitializeStakingConfigInstructionDataArgs,
  InitializeStakingConfigInstructionData
> {
  return combineCodec(
    getInitializeStakingConfigInstructionDataEncoder(),
    getInitializeStakingConfigInstructionDataDecoder(),
  );
}

export type InitializeStakingConfigAsyncInput<
  TAccountStakingConfig extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  stakingConfig?: Address<TAccountStakingConfig>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  minStake: InitializeStakingConfigInstructionDataArgs["minStake"];
  treasury: InitializeStakingConfigInstructionDataArgs["treasury"];
};

export async function getInitializeStakingConfigInstructionAsync<
  TAccountStakingConfig extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: InitializeStakingConfigAsyncInput<
    TAccountStakingConfig,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  InitializeStakingConfigInstruction<
    TProgramAddress,
    TAccountStakingConfig,
    TAccountAuthority,
    TAccountSystemProgram
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.stakingConfig.value) {
    accounts.stakingConfig.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([
            115, 116, 97, 107, 105, 110, 103, 95, 99, 111, 110, 102, 105, 103,
          ]),
        ),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getInitializeStakingConfigInstructionDataEncoder().encode(
      args as InitializeStakingConfigInstructionDataArgs,
    ),
    programAddress,
  } as InitializeStakingConfigInstruction<
    TProgramAddress,
    TAccountStakingConfig,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type InitializeStakingConfigInput<
  TAccountStakingConfig extends string = string,
  TAccountAuthority extends string = string,
  TAccountSystemProgram extends string = string,
> = {
  stakingConfig: Address<TAccountStakingConfig>;
  authority: TransactionSigner<TAccountAuthority>;
  systemProgram?: Address<TAccountSystemProgram>;
  minStake: InitializeStakingConfigInstructionDataArgs["minStake"];
  treasury: InitializeStakingConfigInstructionDataArgs["treasury"];
};

export function getInitializeStakingConfigInstruction<
  TAccountStakingConfig extends string,
  TAccountAuthority extends string,
  TAccountSystemProgram extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: InitializeStakingConfigInput<
    TAccountStakingConfig,
    TAccountAuthority,
    TAccountSystemProgram
  >,
  config?: { programAddress?: TProgramAddress },
): InitializeStakingConfigInstruction<
  TProgramAddress,
  TAccountStakingConfig,
  TAccountAuthority,
  TAccountSystemProgram
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    stakingConfig: { value: input.stakingConfig ?? null, isWritable: true },
    authority: { value: input.authority ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.stakingConfig),
      getAccountMeta(accounts.authority),
      getAccountMeta(accounts.systemProgram),
    ],
    data: getInitializeStakingConfigInstructionDataEncoder().encode(
      args as InitializeStakingConfigInstructionDataArgs,
    ),
    programAddress,
  } as InitializeStakingConfigInstruction<
    TProgramAddress,
    TAccountStakingConfig,
    TAccountAuthority,
    TAccountSystemProgram
  >);
}

export type ParsedInitializeStakingConfigInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    stakingConfig: TAccountMetas[0];
    authority: TAccountMetas[1];
    systemProgram: TAccountMetas[2];
  };
  data: InitializeStakingConfigInstructionData;
};

export function parseInitializeStakingConfigInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedInitializeStakingConfigInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      stakingConfig: getNextAccount(),
      authority: getNextAccount(),
      systemProgram: getNextAccount(),
    },
    data: getInitializeStakingConfigInstructionDataDecoder().decode(
      instruction.data,
    ),
  };
}
